import realtimeAPI from '@/api/realtimeAPI'
import { bus } from './realtimeBus'
import { NativeRealtimeClient } from './realtimeNative'

/** Close the connection after this long without user activity; reopen on focus. */
const IDLE_DISCONNECT_MS = 20 * 60 * 1000

/**
 * RealtimeClient
 *
 * One connection per authenticated user to Edaptio's realtime service
 * (realtime/ in this repo; the protocol client is ./realtimeNative.js). The
 * token from /realtime/token lists every channel the user may hear and the
 * service subscribes the connection to all of them; every event is re-emitted
 * on the global `$realtimeBus` as `{ channel, data, clientId, timestamp }`,
 * and gaps too old to replay arrive as `realtime:resync`.
 *
 * App.vue connects and disconnects it in lockstep with auth state; a board view
 * calls `upgrade('board', id)` to add its `board:<id>` channel. Idle-disconnects
 * after 20 minutes of tab inactivity to keep peak concurrent connections in
 * check; reconnects on tab refocus.
 */
class RealtimeClient {
    constructor() {
        this.native = null
        this._connecting = null
        this.connected = false
        this.idleTimer = null
        this._idleListenersAttached = false
    }

    async connect() {
        if (this.native) return
        // isLoggedIn and window focus can both ask at once; open one connection.
        if (!this._connecting) {
            this._connecting = this._connect().finally(() => { this._connecting = null })
        }
        await this._connecting
    }

    async _connect() {
        let realtime = null
        try {
            ({ realtime } = await realtimeAPI.issueToken())
        } catch (err) {
            realtime = null
        }

        this._startIdleDisconnect()
        // No credential (API unreachable, or the service not configured there):
        // stay offline; the next tab focus tries again.
        if (!realtime) return

        this.native = new NativeRealtimeClient({ bus, api: realtimeAPI })
        this.native.start(realtime)
        this.connected = true
    }

    /**
     * Ask the API for a token that additionally grants `${type}:${id}` (board)
     * and present it on the open connection. Called when the user enters a board.
     */
    async upgrade(type, id) {
        if (!this.native) await this.connect()
        if (!this.native) return
        try {
            await this.native.upgrade(type, id)
        } catch (err) {
            // Non-fatal: the user just won't get live updates for that board.
            // eslint-disable-next-line no-console
            console.warn('[realtime] capability upgrade failed', type, id, err)
        }
    }

    disconnect() {
        if (this.native) {
            this.native.stop()
            this.native = null
        }
        this.connected = false
    }

    /**
     * After 20 min of no user activity, close the connection to keep peak
     * concurrent connections down. Reconnect on tab focus.
     */
    _startIdleDisconnect() {
        if (this._idleListenersAttached || typeof window === 'undefined') return
        this._idleListenersAttached = true

        const resetIdle = () => {
            if (this.idleTimer) clearTimeout(this.idleTimer)
            this.idleTimer = setTimeout(() => this.disconnect(), IDLE_DISCONNECT_MS)
        }

        ;['mousemove', 'keydown'].forEach((e) => window.addEventListener(e, resetIdle, { passive: true }))
        window.addEventListener('focus', () => {
            if (!this.connected) this.connect()
            resetIdle()
        })
        resetIdle()
    }
}

const client = new RealtimeClient()

export default {
    install(app) {
        app.config.globalProperties.$realtime = client
    },
}

export { client }
